import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import SEO from '@/components/feature/SEO';
import ArticleCard from '@/components/feature/ArticleCard';
import { categories, getPopularArticles } from '@/mocks/articles';

const services = [
  {
    icon: 'ri-brain-line',
    title: '心理分析',
    description: '深度剖析婚姻问题的心理根源，帮助你看清问题的本质，找到正确的解决方向。',
    link: '/category/xinli-fenxi',
  },
  {
    icon: 'ri-chat-3-line',
    title: '沟通技巧',
    description: '掌握婚姻中有效沟通的核心方法，打破冷战僵局，重建夫妻间的顺畅对话。',
    link: '/category/goutong-jiqiao',
  },
  {
    icon: 'ri-shield-check-line',
    title: '信任重建',
    description: '系统化的信任修复方案，用真诚的行动逐步消除隔阂，重新建立牢固的情感基础。',
    link: '/category/xinren-zhongjian',
  },
  {
    icon: 'ri-tools-line',
    title: '实操指导',
    description: '可落地的挽回行动方案，从日常细节到关键节点，每一步都有清晰指引。',
    link: '/category/shicao-buzhou',
  },
];

const faqs = [
  {
    q: '妻子提出离婚，我还有挽回的机会吗？',
    a: '只要还没有正式办理离婚手续，就存在挽回的窗口期。关键在于你不能采取错误的挽回方式（如苦苦哀求、威胁、搬出父母施压等）。保持冷静，展示真实的改变，把握住黄金挽回期，许多婚姻都成功实现了逆转。',
  },
  {
    q: '分居后该如何与妻子重新建立联系？',
    a: '分居期间最重要的是"不要纠缠"。给她足够的空间，同时通过自己的实际行动展示改变。可以通过孩子接送、共同朋友等自然途径传递正面信息。当时机成熟时，发出一条简短而真诚的邀请，不要施加压力。',
  },
  {
    q: '挽回婚姻需要多长时间？',
    a: '这取决于婚姻问题的严重程度以及双方的态度。轻度矛盾可能在几周内改善；中度危机通常需要1-3个月的系统努力；严重问题可能需要半年甚至更久。关键是持续真实的改变，而非速成的技巧。',
  },
  {
    q: '我只靠自己看文章就能挽回婚姻吗？',
    a: '本文站的文章提供了系统化的知识和方法论，很多丈夫通过学习本站内容成功改善了婚姻关系。但如果问题特别严重，建议同时寻求专业婚姻咨询师的帮助。知识+行动+专业支持，是最有效的组合。',
  },
  {
    q: '妻子已经对我死心了怎么办？',
    a: '"死心"往往只是一个阶段性的状态，而不是终点。当她说"死心"的时候，通常意味着她过去的期待都落了空。展示真实的、持续的改变——不是口头承诺，而是行动——有可能重新点燃她心中的希望。请相信，人心是可以被真诚打动的。',
  },
];

const stats = [
  { value: '10,000+', label: '服务家庭' },
  { value: '87%', label: '挽回成功率' },
  { value: '12年', label: '行业经验' },
  { value: '500+', label: '真实案例' },
];

export default function Home() {
  const [activeFaq, setActiveFaq] = useState<number | null>(null);
  const popularArticles = useMemo(() => getPopularArticles(6), []);

  const pageSchema = {
    '@context': 'https://schema.org',
    '@type': 'WebSite',
    name: '爱妻艺找回',
    url: 'https://aiqiyi-zhaihui.com/',
    description: '专为已婚男性打造的情感挽回解决方案，系统学习沟通技巧、重建信任、修复婚姻裂痕。',
    potentialAction: {
      '@type': 'SearchAction',
      target: 'https://aiqiyi-zhaihui.com/search?q={search_term_string}',
      'query-input': 'required name=search_term_string',
    },
  };

  const faqSchema = {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: faqs.map((faq) => ({
      '@type': 'Question',
      name: faq.q,
      acceptedAnswer: {
        '@type': 'Answer',
        text: faq.a,
      },
    })),
  };

  return (
    <>
      <SEO
        title="爱妻艺找回 — 专业情感挽回全攻略 | 婚姻修复、挽回妻子、重建信任"
        description="爱妻艺找回是专为已婚男性打造的情感挽回解决方案，系统学习沟通技巧、重建信任、修复婚姻裂痕，让妻子重新爱上你。从心理分析到实操步骤，带你走出婚姻危机，重获幸福家庭。"
        keywords="爱妻艺找回,爱妻艺找回方法,妻子挽回技巧,婚姻修复攻略,找回妻子的心,婚姻危机解决,夫妻感情修复"
        schema={pageSchema}
      />
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(faqSchema) }}
      />
      <Navbar />

      <main>
        <section className="relative w-full min-h-[600px] md:min-h-[700px] flex items-center justify-center overflow-hidden">
          <div className="absolute inset-0">
            <img
              src="https://readdy.ai/api/search-image?query=A%20warm%20and%20intimate%20scene%20of%20a%20couple%20standing%20close%20together%20at%20sunset%20in%20a%20field%20of%20soft%20golden%20grass%2C%20warm%20amber%20and%20rose%20tones%2C%20emotional%20connection%2C%20cinematic%20atmosphere%2C%20backlit%20by%20golden%20hour%20sunlight%2C%20soft%20focus%20background%2C%20artistic%20and%20dreamy%20photography%20style%20with%20shallow%20depth%20of%20field&width=1800&height=900&seq=hero-main-01&orientation=landscape"
              alt="爱妻艺找回 - 情感挽回"
              title="爱妻艺找回 - 专业情感挽回全攻略"
              className="w-full h-full object-cover object-top"
            />
          </div>
          <div className="absolute inset-0 bg-gradient-to-b from-black/35 via-black/20 to-black/40"></div>
          <div className="relative z-10 w-full max-w-4xl mx-auto text-center px-4 md:px-6 py-20">
            <h1 className="text-3xl md:text-5xl lg:text-6xl font-heading font-bold text-white mb-6 leading-tight">
              让妻子重新爱上你
            </h1>
            <p className="text-base md:text-lg text-white/80 mb-10 max-w-2xl mx-auto leading-relaxed">
              爱妻艺找回是专为已婚男性打造的情感挽回解决方案。从心理分析到实操步骤，系统化帮你走出婚姻危机，重获幸福家庭。
            </p>
            <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
              <Link
                to="/category/xinli-fenxi"
                className="inline-flex items-center px-8 py-3.5 text-base font-medium whitespace-nowrap rounded-full bg-primary-500 text-background-50 hover:bg-primary-600 transition-all cursor-pointer"
              >
                开始学习
                <i className="ri-arrow-right-line ml-2"></i>
              </Link>
              <Link
                to="/about"
                className="inline-flex items-center px-8 py-3.5 text-base font-medium whitespace-nowrap rounded-full border border-white/40 text-white hover:bg-white/10 transition-all cursor-pointer"
              >
                了解更多
              </Link>
            </div>
          </div>
        </section>

        <section className="w-full px-4 md:px-6 py-14 md:py-20 bg-background-50">
          <div className="max-w-6xl mx-auto">
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-6 md:gap-8">
              {stats.map((stat, i) => (
                <div key={i} className="text-center">
                  <div className="text-2xl md:text-4xl font-heading font-bold text-primary-600 mb-2">
                    {stat.value}
                  </div>
                  <div className="text-sm text-foreground-600">{stat.label}</div>
                </div>
              ))}
            </div>
          </div>
        </section>

        <section className="w-full px-4 md:px-6 py-14 md:py-20 bg-background-100">
          <div className="max-w-6xl mx-auto">
            <div className="text-center mb-12">
              <h2 className="text-2xl md:text-3xl font-heading font-bold text-foreground-900 mb-4">
                核心服务
              </h2>
              <p className="text-foreground-600 max-w-2xl mx-auto leading-relaxed">
                我们提供系统化的婚姻挽回知识体系，从心理认知到实践行动，全方位助力你修复婚姻关系
              </p>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
              {services.map((service, i) => (
                <Link
                  key={i}
                  to={service.link}
                  className="group bg-background-50 border border-background-200/70 rounded-lg p-6 hover:border-primary-200/70 transition-all duration-300 cursor-pointer"
                >
                  <div className="w-12 h-12 flex items-center justify-center rounded-lg bg-primary-100 text-primary-600 mb-4 group-hover:bg-primary-500 group-hover:text-background-50 transition-colors">
                    <i className={`${service.icon} text-xl`}></i>
                  </div>
                  <h3 className="text-lg font-heading font-semibold text-foreground-900 mb-2">
                    {service.title}
                  </h3>
                  <p className="text-sm text-foreground-600 leading-relaxed">
                    {service.description}
                  </p>
                </Link>
              ))}
            </div>
          </div>
        </section>

        <section className="w-full px-4 md:px-6 py-14 md:py-20 bg-background-50">
          <div className="max-w-6xl mx-auto">
            <div className="flex flex-col sm:flex-row items-end justify-between mb-10 gap-4">
              <div>
                <h2 className="text-2xl md:text-3xl font-heading font-bold text-foreground-900 mb-3">
                  热门文章
                </h2>
                <p className="text-foreground-600">
                  精选最受欢迎的情感挽回文章，帮你快速找到方向
                </p>
              </div>
              <Link
                to="/category/xinli-fenxi"
                className="inline-flex items-center gap-1 text-sm font-medium text-primary-600 hover:text-primary-700 whitespace-nowrap cursor-pointer"
              >
                查看全部
                <i className="ri-arrow-right-line text-xs"></i>
              </Link>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
              {popularArticles.map((article) => (
                <ArticleCard key={article.id} article={article} />
              ))}
            </div>
          </div>
        </section>

        <section className="w-full px-4 md:px-6 py-14 md:py-20 bg-background-100">
          <div className="max-w-3xl mx-auto">
            <div className="text-center mb-12">
              <h2 className="text-2xl md:text-3xl font-heading font-bold text-foreground-900 mb-4">
                常见问题
              </h2>
              <p className="text-foreground-600">
                关于婚姻挽回，你最关心的问题都在这里
              </p>
            </div>
            <div className="flex flex-col gap-3">
              {faqs.map((faq, i) => (
                <div
                  key={i}
                  className="bg-background-50 border border-background-200/70 rounded-lg overflow-hidden"
                >
                  <button
                    className="w-full flex items-center justify-between px-5 py-4 text-left cursor-pointer"
                    onClick={() => setActiveFaq(activeFaq === i ? null : i)}
                  >
                    <span className="text-sm md:text-base font-medium text-foreground-900 pr-4">
                      {faq.q}
                    </span>
                    <i
                      className={`ri-arrow-down-s-line text-lg text-foreground-500 transition-transform duration-300 flex-shrink-0 ${
                        activeFaq === i ? 'rotate-180' : ''
                      }`}
                    ></i>
                  </button>
                  {activeFaq === i && (
                    <div className="px-5 pb-4">
                      <p className="text-sm text-foreground-600 leading-relaxed">
                        {faq.a}
                      </p>
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        </section>

        <section className="relative w-full px-4 md:px-6 py-20 md:py-28 overflow-hidden">
          <div className="absolute inset-0">
            <img
              src="https://readdy.ai/api/search-image?query=Warm%20sunset%20sky%20with%20soft%20golden%20and%20rose%20clouds%2C%20calm%20and%20hopeful%20atmosphere%2C%20abstract%20and%20artistic%20style%2C%20gentle%20gradient%20of%20warm%20tones%2C%20peaceful%20and%20inspiring%20mood%2C%20minimalist%20composition&width=1800&height=600&seq=cta-main-01&orientation=landscape"
              alt=""
              className="w-full h-full object-cover"
            />
          </div>
          <div className="absolute inset-0 bg-gradient-to-b from-black/40 via-black/30 to-black/45"></div>
          <div className="relative z-10 max-w-2xl mx-auto text-center">
            <h2 className="text-2xl md:text-3xl font-heading font-bold text-white mb-4">
              准备好修复你的婚姻了吗？
            </h2>
            <p className="text-white/80 mb-8 leading-relaxed">
              迈出第一步，从今天开始学习和改变。无数丈夫已经通过爱妻艺找回了幸福家庭，你也可以。
            </p>
            <Link
              to="/about"
              className="inline-flex items-center px-8 py-3.5 text-base font-medium whitespace-nowrap rounded-full bg-primary-500 text-background-50 hover:bg-primary-600 transition-all cursor-pointer"
            >
              联系我们
              <i className="ri-arrow-right-line ml-2"></i>
            </Link>
          </div>
        </section>
      </main>

      <Footer />
    </>
  );
}